import React, { useEffect, useState } from "react";
import { BookOpen, PlayCircle, ArrowLeft, Video, CheckCircle2, Circle, Award, Loader2 } from "lucide-react";
import api from "../lib/api";
import { getEmbedUrl } from "../lib/video";
import { Button } from "../components/ui/button";
import { useToast } from "../hooks/use-toast";

export default function StudentLearn() {
  const { toast } = useToast();
  const [courses, setCourses] = useState([]);
  const [course, setCourse] = useState(null);
  const [activeLesson, setActiveLesson] = useState(null);
  const [enrollments, setEnrollments] = useState([]);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const loadEnr = () => api.get("/studio/my-enrollments").then((r) => setEnrollments(r.data)).catch(() => {});
  useEffect(() => { api.get("/studio/published").then((r) => setCourses(r.data)).catch(() => {}); loadEnr(); }, []);

  const enrollment = enrollments.find((e) => e.course_id === course?.id);

  const open = async (id) => {
    const { data } = await api.get(`/studio/published/${id}`);
    setCourse(data);
    setActiveLesson((data.lessons || [])[0] || null);
    setAnswers({}); setResult(null);
  };
  const selectLesson = (l) => { setActiveLesson(l); setAnswers({}); setResult(null); };

  const enroll = async () => { await api.post(`/studio/published/${course.id}/enroll`); await loadEnr(); toast({ title: "Enrolled! 🎓" }); };
  const markComplete = async () => {
    await api.post(`/studio/enrollments/${course.id}/complete`, { lesson_id: activeLesson.id });
    await loadEnr(); toast({ title: "Lesson marked complete" });
  };

  const submitQuiz = async () => {
    setSubmitting(true);
    try {
      const arr = activeLesson.quiz.map((_, i) => (answers[i] !== undefined ? answers[i] : -1));
      const { data } = await api.post(`/studio/published/${course.id}/lessons/${activeLesson.id}/quiz-submit`, { answers: arr });
      setResult(data); loadEnr();
    } catch (e) { toast({ title: "Submit failed", variant: "destructive" }); }
    finally { setSubmitting(false); }
  };

  const isDone = (lid) => enrollment?.progress?.[lid];

  if (course) {
    return (
      <div>
        <button onClick={() => { setCourse(null); setActiveLesson(null); }} className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-[#004030] mb-4"><ArrowLeft size={16} /> All courses</button>
        <div className="grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2">
            <div className="flex items-start justify-between gap-3">
              <div>
                <h2 className="font-display text-2xl font-bold text-[#012a1e]">{course.title}</h2>
                <p className="text-slate-500 text-sm mt-1">{course.subject} · {course.level} · by {course.teacher_name}</p>
              </div>
              {!enrollment && <Button onClick={enroll} className="bg-[#c9a227] hover:bg-[#e0c05a] text-[#013d2b] rounded-full px-5 font-bold shrink-0">Enroll</Button>}
            </div>

            {activeLesson ? (
              <div className="mt-4">
                {activeLesson.video_url && getEmbedUrl(activeLesson.video_url) ? (
                  <div className="aspect-video rounded-xl overflow-hidden border border-slate-200 bg-black">
                    <iframe title={activeLesson.title} src={getEmbedUrl(activeLesson.video_url)} className="w-full h-full" allowFullScreen />
                  </div>
                ) : (
                  <div className="aspect-video rounded-xl bg-[#004030] grid place-items-center text-white/60"><Video size={40} /></div>
                )}
                <div className="flex items-center justify-between mt-4">
                  <h3 className="font-semibold text-[#012a1e] text-lg">{activeLesson.title}</h3>
                  {enrollment && (isDone(activeLesson.id)
                    ? <span className="text-sm text-green-700 font-semibold flex items-center gap-1"><CheckCircle2 size={16} /> Completed</span>
                    : <Button onClick={markComplete} variant="outline" className="rounded-full border-[#004030] text-[#004030] h-9">Mark complete</Button>)}
                </div>
                <div className="text-slate-600 text-sm mt-2 whitespace-pre-wrap leading-relaxed">{activeLesson.content || "No notes for this lesson."}</div>

                {/* Interactive quiz */}
                {(activeLesson.quiz || []).length > 0 && (
                  <div className="mt-6 bg-white rounded-2xl border border-slate-100 p-5">
                    <h4 className="font-display text-lg font-bold text-[#012a1e] flex items-center gap-2"><Award size={18} className="text-[#c9a227]" /> Quiz ({activeLesson.quiz.length} questions)</h4>
                    {result && (
                      <div className={`mt-3 rounded-xl p-4 ${result.score >= 50 ? "bg-green-50 border border-green-200" : "bg-amber-50 border border-amber-200"}`}>
                        <p className="font-bold text-[#012a1e]">Your score: {result.score}% ({result.correct}/{result.total})</p>
                        <p className="text-sm text-slate-600 mt-0.5">{result.score >= 50 ? "Great work! 🎉" : "Keep practising — review the answers below."}</p>
                      </div>
                    )}
                    <div className="space-y-4 mt-4">
                      {activeLesson.quiz.map((q, qi) => (
                        <div key={qi}>
                          <p className="font-medium text-[#012a1e] text-sm">{qi + 1}. {q.q}</p>
                          <div className="mt-2 space-y-1.5">
                            {q.options.map((o, oi) => {
                              const chosen = answers[qi] === oi;
                              const showCorrect = result && oi === q.answer;
                              const showWrong = result && chosen && oi !== q.answer;
                              return (
                                <button key={oi} disabled={!!result} onClick={() => setAnswers({ ...answers, [qi]: oi })}
                                  className={`w-full text-left text-sm px-3 py-2 rounded-lg border transition-colors ${showCorrect ? "border-green-400 bg-green-50 text-green-800" : showWrong ? "border-red-300 bg-red-50 text-red-700" : chosen ? "border-[#004030] bg-[#f0f6f3]" : "border-slate-200 hover:border-[#004030]"}`}>
                                  {o}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      ))}
                    </div>
                    {!result ? (
                      <Button onClick={submitQuiz} disabled={submitting || Object.keys(answers).length < activeLesson.quiz.length} className="mt-4 bg-[#004030] hover:bg-[#013d2b] text-white rounded-full px-6">
                        {submitting ? <Loader2 className="animate-spin" size={16} /> : "Submit Quiz"}
                      </Button>
                    ) : (
                      <Button onClick={() => { setResult(null); setAnswers({}); }} variant="outline" className="mt-4 rounded-full">Retake</Button>
                    )}
                  </div>
                )}
              </div>
            ) : <p className="text-slate-400 mt-6">This course has no lessons yet.</p>}
          </div>

          <div>
            <h3 className="font-semibold text-[#012a1e] mb-3">Lessons</h3>
            <div className="space-y-2">
              {(course.lessons || []).map((l, i) => (
                <button key={l.id} onClick={() => selectLesson(l)} className={`w-full flex items-center gap-3 text-left p-3 rounded-xl border ${activeLesson?.id === l.id ? "border-[#004030] bg-[#f0f6f3]" : "border-slate-100 bg-white hover:bg-[#f7faf8]"}`}>
                  {enrollment ? (isDone(l.id) ? <CheckCircle2 size={18} className="text-green-600 shrink-0" /> : <Circle size={18} className="text-slate-300 shrink-0" />) : <div className="w-7 h-7 rounded-lg bg-[#004030] text-[#e0c05a] grid place-items-center text-xs font-bold shrink-0">{i + 1}</div>}
                  <div className="min-w-0 flex-1"><p className="text-sm font-medium text-[#012a1e] truncate">{l.title}</p>{l.duration && <p className="text-xs text-slate-400">{l.duration}</p>}</div>
                  {(l.quiz || []).length > 0 && <Award size={14} className="text-[#c9a227] shrink-0" />}
                  {l.video_url && <PlayCircle size={15} className="text-[#004030] shrink-0" />}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <h2 className="font-display text-xl font-bold text-[#012a1e]">Learn</h2>
      <p className="text-slate-500 text-sm mt-1">Courses and video lessons from GDIH faculty.</p>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-5">
        {courses.map((c) => {
          const enr = enrollments.find((e) => e.course_id === c.id);
          return (
            <button key={c.id} onClick={() => open(c.id)} className="text-left bg-white rounded-2xl border border-slate-100 p-5 hover-lift">
              <div className="flex items-start justify-between">
                <div className="w-11 h-11 rounded-xl bg-[#f0f6f3] grid place-items-center text-[#004030]"><BookOpen size={20} /></div>
                {enr && <span className="text-[11px] font-semibold px-2.5 py-1 rounded-full bg-green-100 text-green-700">Enrolled</span>}
              </div>
              <p className="font-display text-lg font-bold text-[#012a1e] mt-3">{c.title}</p>
              <p className="text-slate-500 text-sm">{c.subject || "—"} · {c.level || "—"}</p>
              <p className="text-xs text-slate-400 mt-2 flex items-center gap-1"><Video size={12} /> {c.lessons_count} lesson(s) · {c.teacher_name}</p>
            </button>
          );
        })}
        {!courses.length && <p className="sm:col-span-2 lg:col-span-3 text-slate-400 text-center py-10">No published courses yet. Check back soon!</p>}
      </div>
    </div>
  );
}
